import axios from 'lib/axios'

export interface LuminaDescriptiveMapsRequest {
  mail: string
  type: 'lumina_descriptive_maps_request'
  company: string
  message?: string
  contact_name: string
  contact_email: string
  contact_phone: string
  internal_only: true
}

export interface LuminaIntegrationRequest {
  mail: string
  type: 'lumina_integration_request'
  company: string
  message?: string
  contact_name: string
  contact_email: string
  contact_phone: string
  internal_only: false
}

export const requestLuminaDescriptiveMapsInfo = async (payload: LuminaDescriptiveMapsRequest) => {
  const response = await axios.post('/email/send', [payload])
  return response.data
}

export const requestLuminaIntegrationInfo = async (payload: LuminaIntegrationRequest) => {
  const response = await axios.post('/email/send', [payload])
  return response.data
}

export interface LuminaMember {
  member_id: number
  user_id: number
  manager_member_id: number | null
  full_name: string
  document: string | null
  email: string | null
  cargo: string | null
  desempeno?: number | null
  completed_tests?: string[]
}

export interface LuminaTeam {
  id: number
  name: string
  description: string
  member_count?: number
  members?: LuminaMember[]
  source_processes?: LuminaProcess[] | null
}

export interface LuminaTeamPayload {
  name: string
  description: string
  member_ids: number[]
  evaluation_selections?: LuminaEvaluationSelection[]
  person_updates?: LuminaPersonUpdate[]
}

export interface LuminaPersonUpdate {
  user_id: number
  cargo?: string
  desempeno?: number | null
}

export interface LuminaEvaluationSelection {
  user_id: number
  betesa_user_test_id?: number
  skills_user_test_id?: number
}

export interface LuminaMemberEvaluation {
  user_test_id: number
  category_id: number
  process_id: number
  process_name: string
  answer_date: string | null
}

export interface LuminaMemberEvaluationHistory {
  user_id: number
  full_name: string
  document: string | null
  evaluations: LuminaMemberEvaluation[]
}

export interface LuminaProcess {
  id: number
  name: string
  created_at: string
}

export interface LuminaProcessSource {
  process_id: number
  process_name: string
  evaluations: Array<{ user_test_id: number; category_id: number; answer_date: string | null }>
}

export interface LuminaProcessPerson {
  user_id: number
  full_name: string
  document: string | null
  desempeno?: number | null
  cargo?: string | null
  email?: string | null
  sources: LuminaProcessSource[]
}

export interface LuminaProcessExclusion {
  user_id: number
  full_name: string
  process_id: number
  user_test_id: number
  reason: string
}

export interface LuminaProcessPreview {
  processes: LuminaProcess[]
  people: LuminaProcessPerson[]
  excluded: LuminaProcessExclusion[]
  preview_hash: string
}

export interface LuminaProcessCreation {
  team: LuminaTeam
  included: Array<{ user_id: number; full_name: string; process_id: number }>
  excluded: LuminaProcessExclusion[]
}

export const getLuminaProcesses = async (params: {
  search?: string; from?: string; to?: string; page: number; page_size: number
}): Promise<{ items: LuminaProcess[]; total: number }> => {
  const response = await axios.get('/lumina/teams/processes', { params })
  return response.data.data
}

export const previewLuminaProcesses = async (processIds: number[]): Promise<LuminaProcessPreview> => {
  const response = await axios.post('/lumina/teams/processes/preview', { process_ids: processIds })
  return response.data.data
}

export const createLuminaTeamFromProcesses = async (payload: {
  name: string; description: string; process_ids: number[]; preview_hash: string
  selections: Array<{ user_id: number; process_id: number }>
  person_updates?: LuminaPersonUpdate[]
}): Promise<LuminaProcessCreation> => {
  const response = await axios.post('/lumina/teams/from-processes', payload)
  return response.data.data
}

export interface LuminaTeamOrganizationPayload {
  relationships: Array<{ member_id: number; manager_member_id: number | null }>
}

export interface LuminaPerson {
  user_id: number
  full_name: string
  document: string | null
  email: string | null
  cargo: string | null
  completed_tests: string[]
  desempeno: number | null
}

export interface LuminaImportRow {
  row_number: number
  document: string
  cargo: string
  desempeno: string
}

export interface LuminaImportResult {
  row_number: number
  document: string
  status: 'updated' | 'duplicate' | 'invalid' | 'not_found' | 'error'
  reason?: string
  name?: string
  person?: LuminaPerson
}

export interface LuminaImportResponse {
  total: number
  updated: number
  results: LuminaImportResult[]
}

export const importLuminaPeople = async (rows: LuminaImportRow[]): Promise<LuminaImportResponse> => {
  const response = await axios.post('/lumina/people/import', { rows })
  return response.data.data
}

export const getLuminaTeams = async (): Promise<LuminaTeam[]> => {
  const response = await axios.get('/lumina/teams')
  return response.data.data
}

export const getLuminaTeam = async (id: number): Promise<LuminaTeam> => {
  const response = await axios.get(`/lumina/teams/${id}`)
  return response.data.data
}

export const getEligibleLuminaMembers = async (search = ''): Promise<LuminaMember[]> => {
  const response = await axios.get('/lumina/teams/eligible-members', {
    params: search ? { search } : undefined,
  })
  return response.data.data
}

export const getLuminaMemberEvaluations = async (memberIds: number[]): Promise<LuminaMemberEvaluationHistory[]> => {
  const response = await axios.post('/lumina/teams/eligible-evaluations', { member_ids: memberIds })
  return response.data.data
}

export const getLuminaPeople = async (search = ''): Promise<LuminaPerson[]> => {
  const response = await axios.get('/lumina/people', { params: search ? { search } : undefined })
  return response.data.data
}

export const updateLuminaPersonDesempeno = async (userId: number, desempeno: number | null): Promise<void> => {
  await axios.put(`/lumina/people/${userId}/desempeno`, { desempeno })
}

export const updateLuminaPersonCargo = async (userId: number, cargo: string): Promise<void> => {
  await axios.put(`/lumina/people/${userId}/cargo`, { cargo })
}

export const syncTeamWithLumina = async (teamId: number): Promise<any> => {
  const response = await axios.post(`/lumina/teams/${teamId}/lumina/sync`)
  return response.data
}

export const syncTeamWithNinebox = async (teamId: number): Promise<any> => {
  const response = await axios.post(`/lumina/teams/${teamId}/ninebox/sync`)
  return response.data
}

export const createLuminaTeam = async (payload: LuminaTeamPayload): Promise<LuminaTeam> => {
  const response = await axios.post('/lumina/teams', payload)
  return response.data.data
}

export const addLuminaTeamMembers = async (
  id: number, memberIds: number[], evaluationSelections: LuminaEvaluationSelection[] = []
): Promise<{ team_id: number; added_count: number }> => {
  const response = await axios.post(`/lumina/teams/${id}/members`, {
    member_ids: memberIds, evaluation_selections: evaluationSelections,
  })
  return response.data.data
}

export const updateLuminaTeam = async (id: number, payload: LuminaTeamPayload): Promise<LuminaTeam> => {
  const response = await axios.put(`/lumina/teams/${id}`, payload)
  return response.data.data
}

export const deleteLuminaTeam = async (id: number): Promise<void> => {
  await axios.delete(`/lumina/teams/${id}`)
}

export const updateLuminaTeamOrganization = async (
  id: number,
  payload: LuminaTeamOrganizationPayload
): Promise<LuminaTeam> => {
  const response = await axios.put(`/lumina/teams/${id}/organization`, payload)
  return response.data.data
}
